import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { UserProfile } from "@/lib/storage";
import { X } from "lucide-react";

interface Props {
  profile: UserProfile;
  onChange: (profile: UserProfile) => void;
}

export default function SkillsStep({ profile, onChange }: Props) {
  const [skillInput, setSkillInput] = useState("");

  const addSkill = () => {
    const skill = skillInput.trim();
    if (skill && !profile.skills.includes(skill)) {
      onChange({ ...profile, skills: [...profile.skills, skill] });
      setSkillInput("");
    }
  };

  const removeSkill = (skill: string) => {
    onChange({ ...profile, skills: profile.skills.filter(s => s !== skill) });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') { e.preventDefault(); addSkill(); }
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-display font-bold text-lg text-foreground">Skills & Summary</h2>
        <p className="text-sm text-muted-foreground mt-1">Highlight what makes you stand out.</p>
      </div>

      <div>
        <Label className="text-xs text-muted-foreground">Professional Summary</Label>
        <Textarea
          placeholder="A brief overview of your professional background and career objectives..."
          value={profile.summary}
          onChange={e => onChange({ ...profile, summary: e.target.value })}
          className="mt-1 min-h-[80px]"
        />
      </div>

      <div>
        <Label className="text-xs text-muted-foreground">Skills (press Enter to add)</Label>
        <Input
          placeholder="e.g. React, Project Management..."
          value={skillInput}
          onChange={e => setSkillInput(e.target.value)}
          onKeyDown={handleKeyDown}
          className="mt-1"
        />
        {profile.skills.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {profile.skills.map(skill => (
              <span key={skill} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium">
                {skill}
                <button onClick={() => removeSkill(skill)} className="hover:text-destructive transition-colors">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
